/**
 * Maintenance status endpoint (DEV-6343).
 *
 * Public, no-auth probe used to (1) gate the app at bootstrap — BEFORE the JWT context-extraction
 * handshake — and (2) poll while on the maintenance page. Always returns 200.
 *
 * Tenant-aware via the X-Tenant-ID header. During bootstrap the shared axios instance cannot yet read
 * the tenant from {@code window.parentContext} (not set until after the handshake), so the caller may
 * pass {@code tenantId} explicitly and it is sent as the X-Tenant-ID header — mirroring
 * {@code extractJwtContext}.
 */

import type { AxiosRequestConfig } from 'axios';
import axiosInstance from '../axiosInstance';
import { API_TIMEOUTS } from '../../../config/apiTimeouts';

export interface MaintenanceStatus {
  maintenance: boolean;
  pollIntervalMs: number;
}

/** Axios config extended with the shared instance's custom {@code skipAuth} flag. */
interface ProbeRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
}

/**
 * GET /api/v1/maintenance/status
 *
 * @param tenantId optional tenant ID to send as X-Tenant-ID (for the bootstrap probe, before
 *                 window.parentContext is populated). When omitted, the backend checks GLOBAL only.
 * @returns the maintenance flag for the tenant plus the polling cadence (ms).
 */
export async function getMaintenanceStatus(tenantId?: string): Promise<MaintenanceStatus> {
  const headers: Record<string, string> = {};
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  // Public, tenant-aware probe — never send Authorization (skipAuth). A Bearer token here would make
  // the resource server 401 on this no-tenant-context path (DEV-6343).
  const config: ProbeRequestConfig = {
    timeout: API_TIMEOUTS.health,
    headers,
    skipAuth: true,
  };

  const response = await axiosInstance.get('/api/v1/maintenance/status', config);
  // BFF returns the standard envelope: { success, payload, messages }
  const body = (response.data ?? {}) as Record<string, unknown>;
  const payload = (body.payload ?? body) as Record<string, unknown>;
  return {
    maintenance: Boolean(payload.maintenance),
    pollIntervalMs: typeof payload.pollIntervalMs === 'number' ? payload.pollIntervalMs : 300000,
  };
}
